import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import {
  CalendarPlus,
  Plus,
  X,
  Loader2,
  Search,
  Filter,
  Phone,
  Mail,
  MapPin,
  Cake,
  UserPlus,
  Trash2,
  Pencil,
  ArrowRight,
  CheckCircle2,
  Share2,
  Copy,
  Users,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/bookings`;
const CUSTOMERS_API = `${process.env.REACT_APP_BACKEND_URL}/api/customers`;
const PUBLIC_BOOKING_URL = `${window.location.origin}/book`;

const VISIT_OPTIONS = [
  "Only Visit",
  "Eye Test",
  "Lens Only",
  "Frame Only",
  "Complete Specs",
  "Specs Repair",
];

const emptyForm = {
  customer_name: "",
  phone: "",
  email: "",
  date_of_birth: "",
  branch: "Z.A. Optical Johripur",
  visit_for: "Eye Test",
  address: "",
  notes: "",
};

const statusStyle = (s) => {
  switch (s) {
    case "Converted":
      return "bg-emerald-50 text-emerald-700 border-emerald-200";
    case "Cancelled":
      return "bg-red-50 text-red-700 border-red-200";
    default:
      return "bg-blue-50 text-blue-700 border-blue-200";
  }
};

export default function Bookings() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  // Share/QR modal
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  // Select Customer picker
  const [pickerOpen, setPickerOpen] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");

  const openPicker = async () => {
    setPickerOpen(true);
    setPickerSearch("");
    if (customers.length === 0) {
      setCustomerLoading(true);
      try {
        const { data } = await axios.get(CUSTOMERS_API);
        setCustomers(data.customers || []);
      } catch (e) {
        console.error(e);
      } finally {
        setCustomerLoading(false);
      }
    }
  };

  const pickCustomer = (c) => {
    setForm({
      customer_name: c.customer_name || "",
      phone: c.phone || "",
      email: c.email || "",
      date_of_birth: c.date_of_birth || "",
      branch: c.branch || "Z.A. Optical Johripur",
      visit_for: c.visit_for || "Eye Test",
      address: c.address || "",
      notes: form.notes || "",
    });
    setPickerOpen(false);
  };

  const filteredCustomers = useMemo(() => {
    const q = pickerSearch.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter(
      (c) =>
        c.customer_name?.toLowerCase().includes(q) ||
        c.phone?.toLowerCase().includes(q) ||
        c.customer_id?.toLowerCase().includes(q)
    );
  }, [customers, pickerSearch]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(PUBLIC_BOOKING_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // ignore
    }
  };

  const fetchAll = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(API);
      setBookings(data.bookings || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const filtered = useMemo(() => {
    return bookings.filter((b) => {
      const q = search.trim().toLowerCase();
      const matchSearch =
        !q ||
        b.customer_name?.toLowerCase().includes(q) ||
        b.phone?.toLowerCase().includes(q) ||
        b.email?.toLowerCase().includes(q);
      const matchStatus = !statusFilter || b.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [bookings, search, statusFilter]);

  const newCount = bookings.filter((b) => b.status === "New").length;
  const convertedCount = bookings.filter((b) => b.status === "Converted").length;

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (b) => {
    setEditingId(b.id);
    setForm({
      customer_name: b.customer_name || "",
      phone: b.phone || "",
      email: b.email || "",
      date_of_birth: b.date_of_birth || "",
      branch: b.branch || "Z.A. Optical Johripur",
      visit_for: b.visit_for || "Eye Test",
      address: b.address || "",
      notes: b.notes || "",
    });
    setModalOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return alert("Name required.");
    if (!form.phone.trim()) return alert("Phone required.");
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/${editingId}`, form);
      } else {
        await axios.post(API, form);
      }
      setModalOpen(false);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this booking?")) return;
    try {
      await axios.delete(`${API}/${id}`);
      fetchAll();
    } catch {
      alert("Failed to delete.");
    }
  };

  const shiftToCustomer = async (b) => {
    if (
      !window.confirm(
        `Shift "${b.customer_name}" to Customer records? This booking will be removed from Bookings.`
      )
    )
      return;
    setConverting(b.id);
    try {
      const { data } = await axios.post(`${API}/${b.id}/convert`);
      if (data?.customer_id) {
        await fetchAll();
        navigate(`/customer/${data.customer_id}`);
      }
    } catch (err) {
      alert(err.response?.data?.message || "Failed to shift.");
    } finally {
      setConverting(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">
            Bookings
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Quick lead capture — shift to full customer when they visit.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <button
            data-testid="share-booking-link"
            onClick={() => setShareOpen(true)}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-4 text-sm font-medium text-slate-800 hover:bg-slate-50 flex-1 sm:flex-none"
          >
            <Share2 size={16} /> Share Link + QR
          </button>
          <button
            data-testid="new-booking-btn"
            onClick={openAdd}
            className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-5 text-sm font-medium text-white hover:bg-slate-800 flex-1 sm:flex-none"
          >
            <Plus size={17} /> New Booking
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 sm:gap-4">
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Total</p>
          <h2 className="mt-1 text-xl sm:text-3xl font-bold text-slate-900">{bookings.length}</h2>
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">New</p>
          <h2 className="mt-1 text-xl sm:text-3xl font-bold text-blue-600">{newCount}</h2>
        </div>
        <div className="rounded-xl sm:rounded-2xl border border-slate-200 bg-white p-3 sm:p-5 shadow-sm">
          <p className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500">Converted</p>
          <h2 className="mt-1 text-xl sm:text-3xl font-bold text-emerald-600">{convertedCount}</h2>
        </div>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border border-slate-200 bg-white p-3 sm:p-4 shadow-sm flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
          <Search size={16} className="text-slate-400" />
          <input
            data-testid="bookings-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / phone / email"
            className="w-full bg-transparent py-2.5 text-sm outline-none"
          />
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
          <Filter size={16} className="text-slate-400" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-transparent py-2.5 text-sm outline-none pr-4"
          >
            <option value="">All</option>
            <option>New</option>
            <option>Converted</option>
            <option>Cancelled</option>
          </select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
          <Loader2 className="mx-auto animate-spin text-blue-600" size={30} />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-16 text-center">
          <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-slate-100">
            <CalendarPlus className="text-slate-400" size={26} />
          </div>
          <h3 className="mt-3 text-base font-semibold text-slate-700">No bookings yet</h3>
          <p className="text-sm text-slate-500">Create one when a walk-in enquiry comes in.</p>
        </div>
      ) : (
        <div
          data-testid="bookings-list"
          className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4"
        >
          {filtered.map((b) => (
            <div
              key={b.id}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm hover:shadow-md transition"
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="text-base font-semibold text-slate-900">
                    {b.customer_name}
                  </h3>
                  <p className="text-xs text-slate-500">{b.branch} · {b.visit_for || "—"}</p>
                </div>
                <span
                  className={`shrink-0 rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${statusStyle(
                    b.status
                  )}`}
                >
                  {b.status}
                </span>
              </div>

              <div className="mt-3 space-y-1.5 text-sm">
                <p className="flex items-center gap-2 text-slate-700">
                  <Phone size={13} className="text-slate-400" /> {b.phone}
                </p>
                {b.email && (
                  <p className="flex items-center gap-2 text-slate-700 truncate">
                    <Mail size={13} className="text-slate-400" /> {b.email}
                  </p>
                )}
                {b.date_of_birth && (
                  <p className="flex items-center gap-2 text-slate-700">
                    <Cake size={13} className="text-slate-400" /> {b.date_of_birth}
                  </p>
                )}
                {b.address && (
                  <p className="flex items-start gap-2 text-slate-600 text-xs">
                    <MapPin size={13} className="mt-0.5 text-slate-400 shrink-0" />
                    <span className="line-clamp-2">{b.address}</span>
                  </p>
                )}
              </div>

              {b.notes && (
                <p className="mt-3 rounded-lg bg-amber-50 border border-amber-100 p-2 text-xs text-amber-800">
                  {b.notes}
                </p>
              )}

              <div className="mt-4 flex flex-wrap gap-2">
                {b.status === "New" ? (
                  <button
                    data-testid={`shift-booking-${b.id}`}
                    onClick={() => shiftToCustomer(b)}
                    disabled={converting === b.id}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-slate-950 hover:bg-slate-800 text-white px-3 py-2 text-xs font-semibold disabled:opacity-60"
                  >
                    {converting === b.id ? (
                      <Loader2 size={13} className="animate-spin" />
                    ) : (
                      <UserPlus size={13} />
                    )}
                    Shift to Customer
                  </button>
                ) : (
                  b.converted_customer_id && (
                    <button
                      onClick={() => navigate(`/customer/${b.converted_customer_id}`)}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-50 text-emerald-700 hover:bg-emerald-100 px-3 py-2 text-xs font-semibold"
                    >
                      <CheckCircle2 size={13} />
                      View Customer
                      <ArrowRight size={12} />
                    </button>
                  )
                )}
                <button
                  onClick={() => openEdit(b)}
                  className="inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-800 px-3 py-2 text-xs font-semibold"
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => del(b.id)}
                  className="ml-auto inline-flex items-center gap-1 rounded-lg bg-slate-100 hover:bg-red-50 text-slate-600 hover:text-red-700 px-2.5 py-2 text-xs font-semibold"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto"
          onClick={() => !saving && setModalOpen(false)}
        >
          <div
            className="w-full max-w-xl rounded-2xl bg-white shadow-2xl my-6 max-h-[95vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">
                {editingId ? "Edit Booking" : "New Booking"}
              </h3>
              <button
                onClick={() => setModalOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={save} className="p-5 overflow-y-auto space-y-4">
              <button
                type="button"
                data-testid="select-customer-btn"
                onClick={openPicker}
                className="w-full inline-flex items-center justify-center gap-2 rounded-lg border border-dashed border-blue-300 bg-blue-50/60 hover:bg-blue-50 text-blue-700 px-4 py-2.5 text-sm font-semibold"
              >
                <Users size={15} /> Select from existing Customer
              </button>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Customer Name *</label>
                  <input
                    data-testid="booking-name"
                    value={form.customer_name}
                    onChange={(e) =>
                      setForm({ ...form, customer_name: e.target.value })
                    }
                    required
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Phone *</label>
                  <input
                    data-testid="booking-phone"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value.replace(/[^\d]/g, "") })
                    }
                    required
                    inputMode="numeric"
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Email</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Date of Birth</label>
                  <input
                    type="date"
                    value={form.date_of_birth}
                    onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Branch</label>
                  <select
                    value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    <option>Z.A. Optical Johripur</option>
                    <option>Z.A. Optical Jhilmil</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700">Visit For</label>
                  <select
                    value={form.visit_for}
                    onChange={(e) => setForm({ ...form, visit_for: e.target.value })}
                    className="mt-1 w-full h-11 rounded-lg border border-slate-300 px-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  >
                    {VISIT_OPTIONS.map((v) => (
                      <option key={v}>{v}</option>
                    ))}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Address</label>
                  <textarea
                    rows={2}
                    value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="text-xs font-semibold text-slate-700">Notes</label>
                  <textarea
                    rows={2}
                    value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                    className="mt-1 w-full rounded-lg border border-slate-300 p-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    placeholder="Any remarks or reminders"
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end gap-2 pt-2 border-t border-slate-100 mt-3">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  disabled={saving}
                  className="rounded-lg border border-slate-300 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  data-testid="booking-save-btn"
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-950 hover:bg-slate-800 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60"
                >
                  {saving ? <Loader2 className="animate-spin" size={14} /> : null}
                  {editingId ? "Update Booking" : "Save Booking"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Share/QR Modal */}
      {shareOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setShareOpen(false)}
        >
          <div
            data-testid="share-modal"
            className="w-full max-w-md rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">Public Booking Link</h3>
              <button
                onClick={() => setShareOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-sm text-slate-600">
                Share this link or QR — customers can fill their booking themselves.
              </p>
              <div className="grid place-items-center rounded-xl border border-slate-200 bg-white p-4">
                <QRCodeCanvas
                  value={PUBLIC_BOOKING_URL}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#0f172a"
                  level="M"
                  includeMargin
                />
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                <input
                  readOnly
                  value={PUBLIC_BOOKING_URL}
                  className="flex-1 bg-transparent text-xs sm:text-sm text-slate-700 outline-none truncate"
                  data-testid="share-url-input"
                />
                <button
                  data-testid="copy-share-url"
                  onClick={copyLink}
                  className="inline-flex items-center gap-1.5 rounded-md bg-slate-950 hover:bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold shrink-0"
                >
                  {copied ? <CheckCircle2 size={13} /> : <Copy size={13} />}
                  {copied ? "Copied" : "Copy"}
                </button>
              </div>
              <a
                href={PUBLIC_BOOKING_URL}
                target="_blank"
                rel="noreferrer"
                className="block text-center text-xs font-medium text-blue-600 hover:text-blue-700"
              >
                Open in new tab →
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Customer Picker Modal */}
      {pickerOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4"
          onClick={() => setPickerOpen(false)}
        >
          <div
            data-testid="customer-picker"
            className="w-full max-w-lg rounded-2xl bg-white shadow-2xl max-h-[85vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4">
              <h3 className="text-lg font-bold text-slate-900">Select Customer</h3>
              <button
                onClick={() => setPickerOpen(false)}
                className="text-slate-400 hover:text-slate-700"
              >
                <X size={20} />
              </button>
            </div>
            <div className="px-5 pt-4">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3">
                <Search size={16} className="text-slate-400" />
                <input
                  data-testid="picker-search"
                  value={pickerSearch}
                  onChange={(e) => setPickerSearch(e.target.value)}
                  placeholder="Search by name, phone or ID"
                  className="w-full bg-transparent py-2.5 text-sm outline-none"
                  autoFocus
                />
              </div>
            </div>
            <div className="overflow-y-auto p-3 flex-1">
              {customerLoading ? (
                <div className="grid place-items-center py-10">
                  <Loader2 className="animate-spin text-blue-600" size={24} />
                </div>
              ) : filteredCustomers.length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-10">
                  No customers found.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredCustomers.map((c) => (
                    <li key={c.id}>
                      <button
                        type="button"
                        data-testid={`pick-${c.id}`}
                        onClick={() => pickCustomer(c)}
                        className="w-full flex items-center justify-between gap-3 px-3 py-3 text-left hover:bg-slate-50 rounded-lg"
                      >
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-slate-900 truncate">
                            {c.customer_name}
                          </p>
                          <p className="text-xs text-slate-500">
                            {c.phone} · {c.customer_id}
                          </p>
                        </div>
                        <ArrowRight size={14} className="text-slate-400 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
